import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  HISTORY_LABELS, SOURCE_KINDS, buildGrantPayloads, denyPayload, durationLabel, emptyGrant,
  grantableScopes, removableSource, rowStatus, scopeSummary, validateGrant,
} from "./financeAccess";

const TONES = {
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  indigo: "bg-indigo-50 text-indigo-800 border-indigo-200",
  sky: "bg-sky-50 text-sky-800 border-sky-200",
  amber: "bg-amber-50 text-amber-900 border-amber-200",
  rose: "bg-rose-50 text-rose-800 border-rose-200",
};
const STATUS_TONES = {
  allowed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  self_service: "bg-slate-100 text-slate-700 border-slate-200",
  no_scope: "bg-amber-50 text-amber-900 border-amber-200",
  blocked: "bg-rose-50 text-rose-800 border-rose-200",
  not_granted: "bg-white text-slate-500 border-slate-200",
};

const Chip = ({ tone, children, title }) => (
  <span title={title} className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>{children}</span>
);

const SourceLine = ({ source, onRevoke, canChange }) => {
  const kind = SOURCE_KINDS[source.source_type] || { label: source.source_type, tone: "slate", explanation: "" };
  return (
    <li className="flex flex-wrap items-center gap-1.5 text-xs" data-testid={`source-${source.source_type}`}>
      <Chip tone={TONES[kind.tone]} title={kind.explanation}>{kind.label}</Chip>
      <span className="text-slate-700">{source.label}</span>
      {source.scope && <span className="text-slate-500">· {source.scope.label}</span>}
      <span className="text-slate-500">· {durationLabel(source)}</span>
      {source.scope?.resolves === false && source.source_type !== "DIRECT_DENY" && <Chip tone={TONES.amber}>Does not resolve</Chip>}
      {removableSource(source) && canChange && (
        <button type="button" className="ml-auto text-rose-700 underline" onClick={() => onRevoke(source)}>
          {source.source_type === "DIRECT_DENY" ? "Remove block" : "Revoke"}
        </button>
      )}
    </li>
  );
};

const FinanceAccessPanel = ({ userId }) => {
  const [view, setView] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [grant, setGrant] = useState(emptyGrant());
  const [pending, setPending] = useState(null); // { kind: "revoke" | "block", code, source, reason }
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/organisation/users/${userId}/finance-access`);
      setView(data); setError("");
    } catch (e) { setError(formatApiError(e)); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => view?.capabilities || [], [view]);
  const grantableRows = rows.filter((row) => row.grant?.allowed);
  const scopes = useMemo(() => grantableScopes(rows, grant.codes, view?.scope_labels), [rows, grant.codes, view]);
  const units = view?.scope_options?.units || [];
  const canManage = Boolean(view?.administration?.can_manage_target);

  const toggleCode = (code) => setGrant((current) => {
    const codes = current.codes.includes(code) ? current.codes.filter((x) => x !== code) : [...current.codes, code];
    const stillValid = grantableScopes(rows, codes, view?.scope_labels).some((scope) => scope.code === current.scope);
    return { ...current, codes, scope: stillValid ? current.scope : "" };
  });
  const toggleIn = (key, id) => setGrant((current) => ({ ...current, [key]: current[key].includes(id) ? current[key].filter((x) => x !== id) : [...current[key], id] }));

  const submitGrant = async () => {
    const errors = validateGrant(grant, { units });
    if (errors.length) { errors.forEach((message) => toast.error(message)); return; }
    setBusy(true);
    const failures = [];
    for (const payload of buildGrantPayloads(grant)) {
      try { await api.post(`/organisation/users/${userId}/overrides`, payload); }
      catch (e) { failures.push(`${rows.find((row) => row.code === payload.capability)?.label}: ${formatApiError(e)}`); }
    }
    setBusy(false);
    if (failures.length) failures.forEach((message) => toast.error(message));
    else { toast.success("Finance access granted"); setGrant(emptyGrant()); }
    load();
  };

  const confirmPending = async () => {
    if (!pending || pending.reason.trim().length < 5) { toast.error("Record a reason (at least 5 characters) for the audit trail."); return; }
    setBusy(true);
    try {
      if (pending.kind === "block") await api.post(`/organisation/users/${userId}/overrides`, denyPayload(pending.code, pending.reason));
      else await api.post(`/organisation/users/${userId}/overrides/${pending.source.override_id}/revoke`, { reason: pending.reason.trim() });
      toast.success(pending.kind === "block" ? "Blocked for this person" : "Individual access revoked");
      setPending(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (error) return <section className="rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="finance-access-error">Finance access could not be loaded: {error}</section>;
  if (!view) return <section className="rounded border p-4 text-sm text-slate-500">Loading finance access…</section>;

  const anchor = scopes.find((scope) => scope.code === grant.scope)?.anchor;
  const history = rows.flatMap((row) => (row.history || []).map((item) => ({ ...item, label: row.label })));

  return (
    <section className="space-y-4 rounded border p-4" data-testid="finance-access-panel">
      <div>
        <h3 className="font-semibold">Finance access</h3>
        <p className="text-xs text-slate-500">
          What this person can do in Finance, where it applies, until when, and why they have it. Position and appointment authority is inherited and
          managed in Organisation; individual exceptions and blocks are managed here. The server enforces every rule shown.
        </p>
        {!canManage && <p className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="finance-access-readonly">Read-only: {view.administration?.reason}</p>}
      </div>

      <ul className="divide-y rounded border" data-testid="finance-access-rows">
        {rows.map((row) => {
          const status = rowStatus(row);
          const inheritedOnly = row.effective && !row.denied && row.sources.every((source) => !removableSource(source));
          return (
            <li key={row.code} className="space-y-1.5 p-3" data-testid={`finance-row-${row.code}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-sm">{row.label}</span>
                <Chip tone={STATUS_TONES[status.key]}>{status.label}</Chip>
                {row.risk === "high" && <Chip tone={TONES.rose}>High risk</Chip>}
                {row.effective && !row.denied && <span className="text-xs text-slate-500">Applies to: {scopeSummary(row.resolved_scope)}</span>}
                {canManage && row.can_deny && row.effective && !row.denied && (
                  <button type="button" className="ml-auto text-xs text-rose-700 underline" onClick={() => setPending({ kind: "block", code: row.code, reason: "" })}>Block for this person</button>
                )}
              </div>
              <p className="text-xs text-slate-500">{row.description}</p>
              {row.sources.length > 0 && <ul className="space-y-1">{row.sources.map((source, index) => <SourceLine key={`${source.source_type}-${source.override_id || source.assignment_id || source.delegation_id || index}`} source={source} canChange={canManage} onRevoke={(item) => setPending({ kind: "revoke", code: row.code, source: item, reason: "" })} />)}</ul>}
              {inheritedOnly && <p className="text-[11px] text-slate-500">{canManage && row.can_deny ? "Inherited, so it can't be unticked here. Change the position or role, or use “Block for this person”." : "Inherited, so it can't be unticked here. It changes with the person's position or role."}</p>}
              {row.warnings.map((warning) => <p key={warning} className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900" data-testid="finance-warning">{warning}</p>)}
            </li>
          );
        })}
      </ul>

      {pending && (
        <div className="space-y-2 rounded border border-rose-200 bg-rose-50 p-3" data-testid="finance-pending">
          <p className="text-sm font-medium">
            {pending.kind === "block"
              ? `Block “${rows.find((row) => row.code === pending.code)?.label}” for this person? This overrides their position, role and any delegation until the block is removed.`
              : `Revoke “${pending.source.label}” for “${rows.find((row) => row.code === pending.code)?.label}”?`}
          </p>
          <Input placeholder="Required audit reason" value={pending.reason} onChange={(e) => setPending({ ...pending, reason: e.target.value })} />
          <div className="flex gap-2"><Button size="sm" disabled={busy} onClick={confirmPending}>Confirm</Button><Button size="sm" variant="outline" onClick={() => setPending(null)}>Cancel</Button></div>
        </div>
      )}

      {canManage && (
        <div className="space-y-3 rounded border p-3" data-testid="finance-grant">
          <h4 className="text-sm font-semibold">Grant an individual exception</h4>
          <p className="text-xs text-slate-500">Adds authority for this person only, without changing their position or role. Only permissions you can grant are listed.</p>
          {grantableRows.length === 0 ? <p className="text-xs text-slate-500" data-testid="finance-grant-none">You cannot grant any finance permission to this person.</p> : (
            <>
              <fieldset className="grid gap-1 sm:grid-cols-2">
                <legend className="sr-only">Permissions to grant</legend>
                {grantableRows.map((row) => (
                  <label key={row.code} className="flex items-start gap-2 text-sm">
                    <input type="checkbox" className="mt-1" checked={grant.codes.includes(row.code)} onChange={() => toggleCode(row.code)} data-testid={`grant-${row.code}`} />
                    <span>{row.label}{row.effective && !row.denied ? <span className="text-xs text-slate-500"> (already held)</span> : null}</span>
                  </label>
                ))}
              </fieldset>
              <label className="block text-sm">Where it applies
                <select className="mt-1 w-full rounded border p-2" value={grant.scope} disabled={!scopes.length} onChange={(e) => setGrant({ ...grant, scope: e.target.value })} data-testid="grant-scope">
                  <option value="">{grant.codes.length ? "Choose scope" : "Choose permissions first"}</option>
                  {scopes.map((scope) => <option key={scope.code} value={scope.code}>{scope.label}</option>)}
                </select>
              </label>
              {anchor === "unit" && (
                <label className="block text-sm">Organisational unit
                  <select className="mt-1 w-full rounded border p-2" value={grant.unitId} onChange={(e) => setGrant({ ...grant, unitId: e.target.value })} data-testid="grant-unit">
                    <option value="">Choose unit</option>
                    {units.map((unit) => <option key={unit.id} value={unit.id} disabled={!unit.resolves}>{unit.name}{unit.resolves ? "" : " (not configured: no linked programmes/classes)"}</option>)}
                  </select>
                </label>
              )}
              {anchor === "classes" && (
                <fieldset className="max-h-40 overflow-y-auto rounded border p-2 text-sm"><legend className="px-1 text-xs">Classes</legend>
                  {(view.scope_options?.classes || []).map((item) => <label key={item.id} className="flex gap-2"><input type="checkbox" checked={grant.classIds.includes(item.id)} onChange={() => toggleIn("classIds", item.id)} />{item.name}</label>)}
                </fieldset>
              )}
              {anchor === "programmes" && (
                <fieldset className="max-h-40 overflow-y-auto rounded border p-2 text-sm"><legend className="px-1 text-xs">Programmes</legend>
                  {(view.scope_options?.programmes || []).map((item) => <label key={item.id} className="flex gap-2"><input type="checkbox" checked={grant.programIds.includes(item.id)} onChange={() => toggleIn("programIds", item.id)} />{item.name}</label>)}
                </fieldset>
              )}
              <fieldset className="flex flex-wrap items-center gap-3 text-sm"><legend className="sr-only">Duration</legend>
                <label className="flex gap-1"><input type="radio" name="finance-duration" checked={grant.duration === "until_revoked"} onChange={() => setGrant({ ...grant, duration: "until_revoked" })} />Until revoked</label>
                <label className="flex gap-1"><input type="radio" name="finance-duration" checked={grant.duration === "temporary"} onChange={() => setGrant({ ...grant, duration: "temporary" })} data-testid="grant-temporary" />Temporary</label>
                {grant.duration === "temporary" && <>
                  <label className="text-xs">From <Input type="datetime-local" value={grant.startsAt} onChange={(e) => setGrant({ ...grant, startsAt: e.target.value })} /></label>
                  <label className="text-xs">Until <Input type="datetime-local" value={grant.expiresAt} onChange={(e) => setGrant({ ...grant, expiresAt: e.target.value })} data-testid="grant-expires" /></label>
                </>}
              </fieldset>
              <Input placeholder="Required audit reason" value={grant.reason} onChange={(e) => setGrant({ ...grant, reason: e.target.value })} data-testid="grant-reason" />
              <Button disabled={busy} onClick={submitGrant} data-testid="grant-submit">Grant selected</Button>
            </>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div>
          <button type="button" className="text-xs underline" onClick={() => setShowHistory(!showHistory)}>{showHistory ? "Hide" : "Show"} individual grant history ({history.length})</button>
          {showHistory && <ul className="mt-2 space-y-1 text-xs" data-testid="finance-history">{history.map((item) => (
            <li key={item.id} className="rounded border p-2">
              <strong>{item.label}</strong> · {item.effect === "deny" ? "Block" : "Grant"} · {view.scope_labels?.[item.scope] || item.scope} · {durationLabel(item)} · <span>{HISTORY_LABELS[item.status] || item.status}</span>
              {item.reason && <div className="text-slate-500">Reason: {item.reason}</div>}
              {item.revocation_reason && <div className="text-slate-500">Revoked: {item.revocation_reason}</div>}
            </li>
          ))}</ul>}
        </div>
      )}
    </section>
  );
};

export default FinanceAccessPanel;
